import React, { useEffect, useMemo, useState } from 'react';
import { metricsService } from '../services/metricsService';
import { billingService } from '../services/billingService';
import { quotaEnforced, quotaLeftMinutes } from '../lib/quota';
import { IS_SAAS } from '../config/edition';
import OrgMetrics from './OrgMetrics';
import MyLinks from './metrics/MyLinks';

/**
 * Личные метрики: сколько осталось в пакете, что распознано, куда уходит время.
 * Верстка и состав по «Бумаге» (docs/redesign/2026-09-04-bumaga/, артборд
 * «Метрики» и «Метрики · пусто»).
 *
 * 🔴 Экран СВОЙ у каждого человека. Платформенные метрики (вся аудитория,
 * таблица пользователей, разрезы по сервисам) — инструмент администратора и
 * живут в Админке; смешивать их здесь значит показывать сотруднику числа,
 * к которым он не имеет отношения.
 *
 * 🪤 Ряды по дням собираются из СПИСКА ЗАДАЧ человека, а не из `/timeseries`:
 * та ручка агрегирует всю платформу и фильтра по пользователю не знает. Берём
 * до тысячи задач за период; если их больше, график честно говорит, что
 * показывает часть. Заявка на разрез по пользователю —
 * docs/backlog/metrics-user-timeseries.md.
 *
 * Исключение из «экран свой» — плательщик организации: ему переключатель
 * «Мои / Организация» (прогон организаций 24.09 — в «Метриках» он видел только
 * себя, в разделе «Организация» всех, и числа расходились). «Мои» не меняются;
 * «Организация» — OrgMetrics. Участнику переключателя нет: расход коллег —
 * их персональные данные, ручка ответит ему 403.
 */

const RANGES = [[14, '14 дней'], [30, '30 дней']];
// Тысяча задач за период: у активного человека месяц не влезал в пятьсот, и
// график молча показывал часть (замечание владельца 08.09.2026).
const MAX_PAGES = 10;
const PAGE_SIZE = 100;

// Сервисы распознавания. Один файл проходит через несколько (диаризация плюс
// разбор по каналам у стерео), и каждый пишет своё событие с ПОЛНОЙ
// длительностью записи — суммировать их подряд значит считать минуты дважды.
const ASR_SERVICES = new Set([
  'bit-transcribe-v3', 'bit-diarize-v3', 'bit-dual-channel-v3', 'bit-transcribe-parakeet',
  // Живая запись с микрофона — тот же набор, что считает сводка bit-metrics:
  // иначе шапка и график расходятся на минуты записей встреч.
  'bit-streaming',
]);
const SUMMARIZE_SERVICE = 'bit-llm-summarize';
const PLACEHOLDER_NAMES = new Set(['None', 'input.wav']);

// Сколько часов ручной работы экономит час записи. Оценка, а не замер,
// поэтому «≈» и подсказка, как считается. Было 3 (ручная расшифровка вчетверо
// дольше записи): на прогоне 24.09 46 минут встречи дали «4 часа», и это
// читалось как преувеличение. Решение Владимира — консервативнее: два часа на час.
const SAVED_HOURS_PER_HOUR = 2;

// Выбор «Мои / Организация» — удобство одного человека на одном браузере, не
// настройка: пропал localStorage — открываемся на «Мои», как все.
const VIEW_KEY = 'metrics-view:';
function readView(email) {
  try { return window.localStorage.getItem(VIEW_KEY + email); } catch (e) { return null; }
}
function saveView(email, view) {
  try { window.localStorage.setItem(VIEW_KEY + email, view); } catch (e) { /* приватное окно */ }
}

/** Отказ ручки расхода организации — словами для плательщика. */
function orgUsageError(e) {
  const status = e && e.response && e.response.status;
  if (status === 409) {
    return { error: 'У организации нет действующего тарифа — общий расход появится после продления.' };
  }
  if (status === 403) return { error: 'Расход организации виден только плательщику.' };
  return { error: 'Не удалось загрузить расход организации. Ваши личные метрики — в «Мои».', retry: true };
}

const CARD = 'bg-paper-card rounded-card px-6 py-[22px] flex flex-col justify-between';
const CAPTION = 'text-[12px] text-paper-ink-3';

function hoursMinutes(minutes) {
  const m = Math.max(0, Math.round(minutes));
  const h = Math.floor(m / 60);
  return h ? `${h} ч ${m % 60} м` : `${m} м`;
}

/** «2026-09-25» → «25.09». */
export function fmtChartDate(iso) {
  const [, m, d] = String(iso).split('-');
  return d && m ? `${d}.${m}` : String(iso);
}

/** Подпись даты под столбиком: у 14 дней — через день, у 30 — каждый пятый, крайние всегда. */
export function showDateLabel(i, n) {
  if (i === 0 || i === n - 1) return true;
  const step = n > 14 ? 5 : 2;
  // Промежуточная подпись не ближе шага к последней: соседние «26.09» и «27.09»
  // на узком графике слипались в одну строку (телефон, 27.09.2026).
  return i % step === 0 && n - 1 - i >= step;
}

/**
 * Ключ записи для склейки повторов одного файла: имя и размер в байтах.
 *
 * Повтор — те же байты: файл отправлен ещё раз после сбоя или прошёл ещё и
 * диаризацию. Разные звонки под общим именем — разный размер: интеграция
 * «Nestle Purina» (GROTEM) шлёт каждый звонок как `audio.mp3`, и по одному
 * имени сотни звонков за день сливались в одну запись (01.10.2026). Размера
 * нет (старые события, `file_size: null`) — он 0, и склейка идёт по имени, как
 * прежде. Имя-заглушка сервиса — не имя: ключ — task_id. То же правило, что
 * `user_summary_stages` в bit-metrics/app/pipelines.py, — иначе шапка
 * «Распознано за месяц» и столбики под ней расходятся.
 */
export function recordingKey(t) {
  const named = t.file_name && !PLACEHOLDER_NAMES.has(t.file_name);
  return named ? `${t.file_name}\u0000${t.file_size || 0}` : t.task_id;
}

/**
 * Минуты и протоколы по дням.
 *
 * 🔴 Минуты дедуплицируются по `recordingKey` внутри дня (дата UTC). Без этого
 * повтор файла удваивал минуты: получасовая встреча превращалась в час.
 */
export function seriesByDay(tasks, days, now = new Date()) {
  const minutesByDate = new Map();
  const seenPerDate = new Map();     // дата → множество уже засчитанных файлов
  const protocolsByDate = new Map();

  tasks.forEach((t) => {
    if (!t.timestamp) return;
    const key = String(t.timestamp).slice(0, 10);

    if (t.service === SUMMARIZE_SERVICE) {
      // Протокол — только настоящий. Реплики чата, приветствие ассистента и
      // автоимя записи идут тем же сервисом, и «Метрики» показывали девять
      // протоколов при четырёх собранных (прогон 24.09). Задачи без `kind`
      // пришли от bit-metrics до этой правки — считаем как прежде.
      if ((t.kind === undefined || t.kind === 'summary') && !t.error) {
        protocolsByDate.set(key, (protocolsByDate.get(key) || 0) + 1);
      }
      return;
    }
    if (!ASR_SERVICES.has(t.service) || !t.audio_seconds) return;

    const seen = seenPerDate.get(key) || new Set();
    const id = recordingKey(t);
    if (id && seen.has(id)) return;
    if (id) seen.add(id);
    seenPerDate.set(key, seen);
    minutesByDate.set(key, (minutesByDate.get(key) || 0) + t.audio_seconds / 60);
  });

  const out = [];
  for (let i = days - 1; i >= 0; i -= 1) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    out.push({
      date: key,
      minutes: Math.round(minutesByDate.get(key) || 0),
      protocols: protocolsByDate.get(key) || 0,
    });
  }
  return out;
}

/**
 * Самые длинные записи: по длительности, без повторов одного файла
 * (`recordingKey`). Имя строки не уникально — у интеграции все звонки
 * `audio.mp3`, — поэтому React-ключ строки — `key`, а не `name`.
 */
export function longestRecordings(tasks, limit = 10) {
  const best = new Map();
  tasks.forEach((t) => {
    if (!t.file_name || !t.audio_seconds) return;
    const key = recordingKey(t) ?? t.file_name;
    const seen = best.get(key);
    if (!seen || seen.seconds < t.audio_seconds) {
      best.set(key, { name: t.file_name, seconds: t.audio_seconds });
    }
  });
  return [...best.entries()]
    .sort((a, b) => b[1].seconds - a[1].seconds)
    .slice(0, limit)
    .map(([key, { name, seconds }]) => ({ key, name, minutes: Math.round(seconds / 60) }));
}

export default function UserMetrics({ email, onGoToRecognition, onGoToPricing }) {
  const [range, setRange] = useState(14);
  const [tasks, setTasks] = useState(null);
  const [truncated, setTruncated] = useState(false);
  const [quota, setQuota] = useState(null);
  const [summary, setSummary] = useState(null);
  const [protocols, setProtocols] = useState(null);
  const [error, setError] = useState(null);
  // Организация, где человек — плательщик; иначе null и переключателя нет.
  const [org, setOrg] = useState(null);
  const [view, setView] = useState('mine');
  // null — не спрашивали или едет; { data } или { error, retry }.
  const [orgUsage, setOrgUsage] = useState(null);

  useEffect(() => {
    // В издании onprem bit-billing нет — и организаций тоже.
    if (!email || !IS_SAAS) return undefined;
    let alive = true;
    billingService.getMyOrganization()
      .then((d) => {
        const o = d && d.organization;
        // Замороженную показываем выключенным переключателем с объяснением:
        // пропавший пункт плательщик прочтёт как поломку, а не как конец тарифа.
        if (!alive || !o || !o.is_owner || !['active', 'frozen'].includes(o.state)) return;
        setOrg(o);
        if (o.state === 'active' && readView(email) === 'org') setView('org');
      })
      // Биллинг не ответил — просто нет переключателя; «Мои» работают как прежде.
      .catch(() => {});
    return () => { alive = false; };
  }, [email]);

  useEffect(() => {
    // Спрашиваем расход только когда его смотрят: большинство открывает «Мои».
    if (view !== 'org' || !org || org.state !== 'active' || orgUsage) return undefined;
    let alive = true;
    metricsService.getOrgUsage(org.id)
      .then((data) => { if (alive) setOrgUsage({ data }); })
      .catch((e) => { if (alive) setOrgUsage(orgUsageError(e)); });
    return () => { alive = false; };
  }, [view, org, orgUsage]);

  const chooseView = (next) => {
    setView(next);
    saveView(email, next);
  };

  useEffect(() => {
    if (!email) return;
    metricsService.getQuota(email).then(setQuota).catch(() => {});
    metricsService.getUserSummary(email).then(setSummary).catch(() => {});
    metricsService.getUserTasks(email, { service: 'bit-llm-summarize', kind: 'summary', page_size: 1 })
      .then((d) => setProtocols(d.total_count ?? 0))
      .catch(() => {});
  }, [email]);

  useEffect(() => {
    if (!email) return;
    let alive = true;
    const from = new Date();
    from.setDate(from.getDate() - range);
    (async () => {
      const collected = [];
      let page = 1;
      let more = true;
      try {
        while (more && page <= MAX_PAGES) {
          // eslint-disable-next-line no-await-in-loop
          const data = await metricsService.getUserTasks(email, {
            date_from: from.toISOString(), page, page_size: PAGE_SIZE,
          });
          collected.push(...(data.tasks || []));
          more = collected.length < (data.total_count || 0);
          page += 1;
        }
        if (!alive) return;
        setTasks(collected);
        setTruncated(more);
      } catch (e) {
        if (alive) setError('Не удалось загрузить статистику');
      }
    })();
    return () => { alive = false; };
  }, [email, range]);

  const days = useMemo(() => seriesByDay(tasks || [], range), [tasks, range]);
  const longest = useMemo(() => longestRecordings(tasks || []), [tasks]);
  // Шкала графика — только минуты: протоколы подписаны цифрой под днём, общая ось
  // прижала бы их к нулю (десятки минут против единиц документов).
  const maxMinutes = Math.max(1, ...days.map((d) => d.minutes));

  const left = quotaLeftMinutes(quota);
  const limit = quota?.audio?.limit ?? null;
  // Сотруднику лимит — ориентир, а не потолок: перерасход даёт предупреждение
  // в логе, работу он не останавливает. Поэтому карточка показывает ему
  // расход, а не «осталось», и полоса наполняется, а не убывает.
  const enforced = quotaEnforced(quota);
  const used = Math.round(quota?.audio?.used ?? 0);
  const quotaPct = limit
    ? Math.max(0, Math.min(100, Math.round(((enforced ? (left ?? 0) : used) / limit) * 100)))
    : null;
  const quotaLow = enforced && quotaPct !== null && quotaPct <= 20;

  const audioMinutes = summary?.audio_minutes ?? 0;
  const recordings = summary?.total_tasks ?? 0;
  const saved = Math.round((audioMinutes * SAVED_HOURS_PER_HOUR) / 60);

  // Пусто — это когда человек ещё ничего не распознавал, а не когда данные едут.
  // Протоколы из вставленного текста — тоже работа: при них «статистика
  // появится после первой записи» противоречила бы числу протоколов рядом.
  const empty = summary && recordings === 0 && !protocols;

  const kpis = [
    // Подписи «N записей» и «из N записей» убраны (решение 24.09): число
    // задач человеку ничего не говорит, а рядом с минутами путает.
    {
      label: 'Распознано за месяц',
      value: hoursMinutes(audioMinutes),
      sub: '',
    },
    {
      label: 'Протоколов',
      value: protocols === null ? '—' : String(protocols),
      sub: '',
    },
    {
      label: 'Сэкономлено времени',
      value: `≈ ${saved} ч`,
      sub: 'ручная расшифровка — около двух часов на час записи',
    },
  ];

  const orgFrozen = org && org.state === 'frozen';
  const segment = (active) => `rounded-chip px-3 py-[6px] text-[13px] font-medium transition-colors ${
    active ? 'bg-paper-ink text-white' : 'text-paper-ink hover:bg-paper-panel disabled:text-paper-hint disabled:hover:bg-transparent disabled:cursor-not-allowed'
  }`;
  const viewSwitch = org && (
    <div className="flex flex-wrap items-center gap-3 shrink-0">
      <div role="tablist" aria-label="Чьи метрики" className="inline-flex gap-1 rounded-chip bg-paper-card p-1">
        <button type="button" role="tab" aria-selected={view === 'mine'}
                onClick={() => chooseView('mine')} className={segment(view === 'mine')}>
          Мои
        </button>
        <button type="button" role="tab" aria-selected={view === 'org'} disabled={orgFrozen}
                onClick={() => chooseView('org')} className={segment(view === 'org')}>
          Организация
        </button>
      </div>
      {orgFrozen && (
        <span data-testid="org-metrics-frozen" className={CAPTION}>
          Тариф организации закончился — общий расход появится после продления.
        </span>
      )}
    </div>
  );

  if (view === 'org' && org && !orgFrozen) {
    return (
      <div className="flex-1 min-h-0 flex flex-col gap-3">
        {viewSwitch}
        <OrgMetrics org={org} usage={orgUsage} onRetry={() => setOrgUsage(null)} />
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 flex flex-col gap-3">
      {viewSwitch}
      {error && (
        <div className="rounded-panel bg-paper-accent/[0.06] px-4 py-3 text-[13px] text-paper-accent shrink-0">
          {error}
        </div>
      )}

      <div className="grid gap-3 shrink-0 grid-cols-[minmax(0,1fr)] lg:grid-cols-[1.4fr_1fr_1fr_1fr]">
        <div className={CARD}>
          <div className={CAPTION}>{enforced ? 'Осталось в пакете' : 'Расход за 30 дней'}</div>
          <div className="my-[10px] font-display text-[56px] font-extrabold leading-none tracking-[-0.04em] text-paper-ink whitespace-nowrap">
            {limit === null ? '∞' : (enforced ? left : used)}
            <span className="ml-2 text-[20px] font-semibold tracking-[-0.02em] text-paper-ink-3">
              {limit ? (enforced ? `из ${limit} мин` : `ориентир ${limit} мин`) : 'без лимита'}
            </span>
          </div>
          {quotaPct !== null && (
            <div className="h-[6px] rounded-[3px] bg-paper-bg overflow-hidden">
              <div className="h-full bg-paper-accent" style={{ width: `${quotaPct}%` }} />
            </div>
          )}
          <div className="mt-[10px] text-[12px] text-paper-ink-2">
            {!enforced
              ? 'Мягкий ориентир: расшифровку не останавливает'
              : quotaLow ? 'Пакет почти закончился' : 'Пакет обновится в начале месяца'}
          </div>
        </div>

        {kpis.map((k) => (
          <div key={k.label} className={CARD}>
            <div className={CAPTION}>{k.label}</div>
            <div className="mt-[10px] font-display text-[40px] font-extrabold leading-none tracking-[-0.04em] text-paper-ink whitespace-nowrap">
              {k.value}
            </div>
            <div className={`mt-2 ${CAPTION}`}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Ждём сводку: пока не знаем, есть ли у человека записи, не знаем и то,
          есть ли ему чем делиться. Без единой записи строки нет. */}
      {summary && !empty && <MyLinks />}

      {empty ? (
        <div className="flex-1 bg-paper-card rounded-card flex flex-col items-center justify-center text-center gap-[14px] p-10">
          <div className="font-display text-[28px] font-extrabold tracking-[-0.03em] text-paper-ink">
            Статистика появится после первой записи
          </div>
          <p className="m-0 max-w-[44ch] text-[14px] text-paper-ink-2">
            Здесь будут минуты по дням, самые длинные записи и то, сколько осталось в пакете.
          </p>
          <button type="button" onClick={onGoToRecognition}
            className="rounded-control bg-paper-accent hover:bg-paper-accent-hover px-[22px] py-3 text-[14px] font-semibold text-white transition-colors">
            Распознать первую
          </button>
        </div>
      ) : (
        <div className="grid shrink-0 md:shrink md:flex-1 md:min-h-0 gap-3 grid-cols-[minmax(0,1fr)] lg:grid-cols-[minmax(0,1fr)_340px]">
          {/* На телефоне листается вся страница, а не карточки: растянутая на
              высоту экрана сетка сжимала график, и столбики ложились на соседнюю. */}
          <div className="bg-paper-card rounded-card px-4 md:px-6 py-[22px] flex flex-col min-h-0">
            <div className="flex items-baseline justify-between gap-3">
              <span className={CAPTION}>Минуты по дням</span>
              <div className="flex gap-1">
                {RANGES.map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setRange(value)}
                    className={`rounded-chip px-[10px] py-1 text-[12px] font-medium transition-colors ${
                      range === value ? 'bg-paper-ink text-white' : 'bg-paper-bg text-paper-ink hover:bg-paper-panel'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Один ряд — минуты по дням, с осью и датами; протоколы — малиновой
                цифрой под столбиком. Два столбика рядом со своими шкалами без
                осей не читались (тест владельца 25.09). */}
            <div data-testid="minutes-chart" className="flex flex-1 min-h-[160px] pt-6 gap-2">
              <div className="flex flex-col justify-between items-end pb-[34px] text-[11px] text-paper-ink-3 tabular-nums">
                <span>{maxMinutes}</span>
                <span>{Math.round(maxMinutes / 2)}</span>
                <span>0</span>
              </div>
              {/* min-w-0 у ряда и у колонок: иначе подпись «14.09» задаёт колонке
                  минимальную ширину, и на телефоне 14 колонок уезжали за край. */}
              <div className="flex-1 min-w-0 flex flex-col">
                <div className={`flex flex-1 items-end border-b border-l border-paper-line ${range === 14 ? 'gap-1 md:gap-[10px]' : 'gap-1'}`}>
                  {days.map((d) => (
                    <div key={d.date} title={`${fmtChartDate(d.date)}: ${d.minutes} мин, протоколов ${d.protocols}`}
                         className="flex-1 min-w-0 h-full flex items-end">
                      <div
                        className={`w-full rounded-t-[4px] ${d.minutes ? 'bg-paper-ink' : 'bg-paper-bg'}`}
                        style={{ height: `${Math.max(2, Math.round((d.minutes / maxMinutes) * 100))}%` }}
                      />
                    </div>
                  ))}
                </div>
                {/* border-l прозрачный — тот же 1 px, что у оси сверху: без него
                    подписи съезжали влево относительно столбиков. */}
                <div className={`flex pt-1 border-l border-transparent ${range === 14 ? 'gap-1 md:gap-[10px]' : 'gap-1'}`}>
                  {days.map((d, i) => (
                    <div key={d.date} className="flex-1 min-w-0 flex flex-col items-center text-[10px] leading-[14px] tabular-nums whitespace-nowrap">
                      <span data-testid="chart-protocols" className="font-semibold text-paper-accent">
                        {d.protocols ? d.protocols : '\u00a0'}
                      </span>
                      <span className="text-paper-ink-3">
                        {showDateLabel(i, days.length) ? fmtChartDate(d.date) : '\u00a0'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-4 pt-2 text-[12px] text-paper-ink-2">
              <span className="flex items-center gap-[6px]">
                <span className="w-[10px] h-[10px] rounded-[3px] bg-paper-ink" />
                Минуты распознавания за день
              </span>
              <span className="flex items-center gap-[6px]">
                <span className="font-semibold text-paper-accent">N</span>
                Протоколов за день
              </span>
            </div>

            {truncated && (
              <div className={CAPTION}>
                Показаны последние {MAX_PAGES * PAGE_SIZE} задач — за период их больше.
              </div>
            )}
          </div>

          <div className="bg-paper-card rounded-card px-6 py-[22px] flex flex-col gap-[14px] min-h-0">
            <div className={CAPTION}>Самые длинные записи</div>
            {longest.length === 0 && (
              <div className="text-[13px] text-paper-ink-3">За этот период записей нет.</div>
            )}
            {longest.map((r) => (
              <div key={r.key} className="flex justify-between gap-3 text-[13px]">
                <span className="truncate text-paper-ink" title={r.name}>{r.name}</span>
                <span className="font-mono text-paper-ink-3 whitespace-nowrap">{r.minutes} мин</span>
              </div>
            ))}

            {quotaLow && onGoToPricing && (
              <div className="mt-auto rounded-control bg-paper-bg px-4 py-[14px] text-[13px]">
                <b className="font-semibold text-paper-ink">Пакет почти закончился.</b>{' '}
                <button type="button" onClick={onGoToPricing}
                        className="underline underline-offset-[3px] text-paper-ink">
                  Пополнить
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
